import React, { useRef } from 'react';
import { Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { categoryChips } from '../mock/mockData';

const CategoryChips = ({ active, onChange, notificationCount = 0 }) => {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 200, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-14 z-30 bg-white dark:bg-[#0f0f0f] py-3">
      <div className="relative">
        <button
          onClick={() => scroll(-1)}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white dark:bg-[#0f0f0f] shadow-md rounded-full"
          aria-label="Scroll left"
        >
          <ChevronLeft size={18} />
        </button>
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scrollbar-hide px-2 md:px-10"
          style={{ scrollbarWidth: 'none' }}
        >
          {categoryChips.map(chip => {
            const isNotifications = chip === 'Notifications';
            const isActive = active === chip;
            // Notifications chip is icon-only — a bell with an integrated
            // count badge (top-right corner of the bell). Sizing kept in
            // step with the text chips: total width ≈ text-chip height so
            // horizontal rhythm along the row stays consistent.
            if (isNotifications) {
              return (
                <button
                  key={chip}
                  onClick={() => onChange(chip)}
                  data-testid="chip-notifications"
                  aria-label={`Notifications${notificationCount ? ` (${notificationCount})` : ''}`}
                  title="Notifications"
                  className={`relative flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                      : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-200 dark:hover:bg-zinc-700'
                  }`}
                >
                  <Bell size={18} strokeWidth={2.25} />
                  {notificationCount > 0 && (
                    <span
                      data-testid="chip-notifications-badge"
                      className="absolute top-1/2 -translate-y-1/2 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none ring-2 ring-white dark:ring-[#0f0f0f]"
                    >
                      {notificationCount > 99 ? '99+' : notificationCount}
                    </span>
                  )}
                </button>
              );
            }
            return (
              <button
                key={chip}
                onClick={() => onChange(chip)}
                data-testid={`chip-${chip.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-white hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }`}
              >
                <span>{chip}</span>
              </button>
            );
          })}
        </div>
        <button
          onClick={() => scroll(1)}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white dark:bg-[#0f0f0f] shadow-md rounded-full"
          aria-label="Scroll right"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default CategoryChips;
